import { useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { entityTrack } from "@/lib/entities";
import { EASE, fmtPct, fmtUsd, pctColor, timeAgo } from "./nl";
import { AnimatedMetric } from "./AnimatedMetric";

/* DEX ACTIVITY MODULE v1 — canonical NewsLayer DEX intelligence surface.
   Profile-driven (identity.profile === "dex"), never name-driven.
   Real provider volume history only: daily series for 7D–1Y, exact monthly
   sums for ALL. VOLUME ≠ TVL ≠ FEES. No fee-yield metric by founder directive. */

const RANGES = ["7D", "30D", "90D", "1Y", "ALL"] as const;
const DAYS: Record<string, number> = { "7D": 7, "30D": 30, "90D": 90, "1Y": 366 };
const W = 790, H = 230;

const monthLabel = (m: string) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
};
const dayLabel = (d: string) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const ShareRow = ({ r, color, testid }: { r: any; color: string; testid: string }) => (
  <div className="py-[8px] border-t border-[#14161f] first:border-0" data-testid={testid}>
    <div className="flex items-baseline justify-between gap-[10px]">
      <p className="nl-geist text-[12.5px] leading-[16px] font-semibold text-[#f6f7fc] truncate">{r.name}</p>
    <p className="nl-mono text-[9.5px] leading-[14px] text-[#9696a8] whitespace-nowrap">
        {fmtUsd(r.volume_usd)} · <span className="text-white font-semibold">{r.share_pct}%</span>
      </p>
    </div>
    <div className="mt-[6px] h-[4px] rounded-full bg-white/[0.05] overflow-hidden">
      <div className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${Math.min(r.share_pct, 100)}%`, background: color }} />
    </div>
  </div>
);

export const EntityDexActivity = ({ overview }: { overview: any }) => {
  const reduced = useReducedMotion();
  const da = overview.dex_activity;
  const id = overview.identity;
  const [range, setRange] = useState<string>("30D");
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const rows = useMemo(() => {
    if (!da || da.error) return [];
    if (range === "ALL")
      return (da.history_monthly || []).map((r: any) => ({ label: monthLabel(r.month), v: r.volume_usd }));
    return (da.history_daily || []).slice(-DAYS[range]).map((r: any) => ({ label: dayLabel(r.date), v: r.volume_usd }));
  }, [da, range]);
  const maxV = useMemo(() => Math.max(1, ...rows.map((r: any) => r.v || 0)), [rows]);
  const total = useMemo(() => rows.reduce((s: number, r: any) => s + (r.v || 0), 0), [rows]);

  if (!da) return null;
  if (da.error) {
    return (
      <div className="rounded-[22px] bg-[#06060a] border border-[#222234] p-[24px]" data-testid="dex-activity-delayed">
        <p className="nl-geist text-[13px] leading-[19px] text-[#9499ad]" role="status">
          DEX activity data is temporarily delayed — the provider could not be reached. Nothing is estimated in its place.
        </p>
      </div>
    );
  }

  const v = da.volume || {};
  const ms = da.market_share;
  const n = rows.length;
  const step = n ? W / n : W;
  const bw = Math.max(1.2, step * 0.62);
  const yOf = (val: number) => H - 6 - ((val || 0) / (maxV * 1.05)) * (H - 26);

  const setR = (r: string) => {
    setRange(r);
    setHover(null);
    entityTrack("chart_range_change", id.entity_id, { chart: "dex_volume", range: r });
  };
  const onMove = (e: React.MouseEvent) => {
    if (!svgRef.current || !n) return;
    const rect = svgRef.current.getBoundingClientRect();
    const i = Math.floor(((e.clientX - rect.left) / rect.width) * n);
    setHover(Math.max(0, Math.min(n - 1, i)));
  };
  const hoverRow = hover != null ? rows[hover] : null;

  const topChain = (da.chain_breakdown_24h || [])[0];
  const topVersion = (da.version_breakdown_24h || [])[0];

  return (
    <div className="rounded-[22px] bg-[#06060a] border border-[#222234] p-[20px] md:p-[30px]"
      style={{ boxShadow: "0 0 50px rgba(34,211,238,0.10)" }} data-testid="dex-activity">
      <p className="sr-only" data-testid="dex-sr-summary">
        {id.name} 24-hour trading volume is {fmtUsd(v.total_24h_usd)}
        {ms ? `, ranked number ${ms.rank_by_24h} with ${ms.share_pct} percent of all DEX volume` : ""}.
        {topChain ? ` Top chain: ${topChain.name} at ${topChain.share_pct} percent.` : ""}
        {topVersion ? ` Top version: ${topVersion.name} at ${topVersion.share_pct} percent.` : ""}
      </p>

      <div className="flex flex-wrap items-start justify-between gap-x-[20px] gap-y-[14px]">
        <div>
          <div className="flex items-baseline gap-[12px]">
            <AnimatedMetric value={v.total_24h_usd} format={fmtUsd} baseColor="#ffffff"
              className="nl-sg block text-[28px] md:text-[34px] leading-[40px] font-medium tracking-[-0.01em]"
              testid="dex-volume-current" />
            {v.change_24h_pct != null && (
              <span className="nl-geist text-[12px] font-semibold" style={{ color: pctColor(v.change_24h_pct) }}
                data-testid="dex-volume-change">
                {fmtPct(v.change_24h_pct)}
              </span>
            )}
          </div>
          <p className="nl-geist text-[9px] leading-[13px] font-semibold tracking-[0.9px] text-[#9696a8] uppercase mt-[4px]">
            24H Trading Volume
          </p>
        </div>
        <div className="flex flex-col items-end gap-[8px]">
          {ms && (
            <span className="nl-mono text-[8.5px] md:text-[9.5px] leading-[13px] font-semibold tracking-[0.08em] uppercase text-[#22d3ee] border border-[#22d3ee]/40 rounded-[9px] px-[11px] py-[7px]"
              data-testid="dex-market-position">
              {ms.rank_by_24h ? `#${ms.rank_by_24h} DEX · ` : ""}{ms.share_pct}% of all DEX volume
            </span>
          )}
          <p className="nl-mono text-[9px] leading-[14px] font-medium text-[#9696a8] text-right hidden sm:block">
            SOURCE · DEFILLAMA DEX DATASET<br />{timeAgo(da.fetched_at)}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-[24px] gap-y-[6px] mt-[16px] nl-mono text-[10px] leading-[14px] font-medium text-[#9696a8]">
        <span>7D · <span className="text-white">{fmtUsd(v.total_7d_usd)}</span></span>
        <span>30D · <span className="text-white">{fmtUsd(v.total_30d_usd)}</span></span>
        <span className="hidden sm:inline">1Y · <span className="text-white">{fmtUsd(v.total_1y_usd)}</span></span>
        <span>ALL-TIME · <span className="text-white">{fmtUsd(v.all_time_usd)}</span></span>
      </div>

      <div className="flex gap-[10px] mt-[22px]" role="tablist" aria-label="Volume range">
        {RANGES.map((r) => (
          <button key={r} role="tab" aria-selected={range === r} onClick={() => setR(r)}
            data-testid={`dex-range-${r.toLowerCase()}`}
            className={`nl-geist w-[40px] md:w-[48px] h-[25px] md:h-[28px] rounded-[8px] md:rounded-[9px] text-[10px] font-semibold tracking-[0.4px] border transition-colors duration-200 ${
              range === r ? "bg-[#0e1418] border-[#22d3ee] text-[#22d3ee]" : "border-[#222234] text-[#9696a8] hover:text-white"
            } ${r === "90D" ? "hidden sm:block" : ""}`}>{r}</button>
        ))}
      </div>

      <div className="mt-[24px] relative" onMouseLeave={() => setHover(null)}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
          className="w-full h-[190px] md:h-[230px]" onMouseMove={onMove} role="img"
          aria-label={`${range} trading volume history. Range total ${fmtUsd(total)}, peak ${fmtUsd(maxV)}.`}
          data-testid="dex-volume-chart">
          <defs>
            <linearGradient id="nl-dex-bar" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(34,211,238,0.9)" />
              <stop offset="100%" stopColor="rgba(139,92,255,0.16)" />
            </linearGradient>
          </defs>
          <g opacity={0.5}>
            {[0.25, 0.5, 0.75].map((f) => (
              <line key={f} x1={0} x2={W} y1={H * f} y2={H * f} stroke="#242437" strokeWidth={1} vectorEffect="non-scaling-stroke" />
            ))}
          </g>
          {rows.map((r: any, i: number) => (
            <motion.rect key={`${range}-${i}`} x={i * step + (step - bw) / 2} width={bw}
              initial={reduced ? false : { y: H - 6, height: 0 }}
              animate={{ y: yOf(r.v), height: Math.max(1, H - 6 - yOf(r.v)) }}
              transition={reduced ? { duration: 0.1 } : { duration: 0.34, delay: Math.min(i * 0.004, 0.25), ease: EASE }}
              fill="url(#nl-dex-bar)" opacity={hover === i ? 1 : 0.78} rx={Math.min(2, bw / 3)} />
          ))}
          {hoverRow && (
            <line x1={hover! * step + step / 2} x2={hover! * step + step / 2} y1={0} y2={H}
              stroke="#22d3ee" strokeWidth={1} opacity={0.45} vectorEffect="non-scaling-stroke" />
          )}
        </svg>
        {hoverRow && (
          <div className="absolute top-[6px] pointer-events-none rounded-[10px] bg-[#0a0a10] border border-[#222234] px-[12px] py-[8px]"
            style={{ left: `clamp(0px, calc(${((hover! + 0.5) / n) * 100}% - 60px), calc(100% - 140px))` }}
            data-testid="dex-volume-tooltip">
            <p className="nl-sg text-[15px] leading-[18px] font-medium text-white">{fmtUsd(hoverRow.v)}</p>
            <p className="nl-mono text-[9px] leading-[13px] font-medium text-[#9696a8] mt-[2px]">{hoverRow.label.toUpperCase()}</p>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-x-[28px] gap-y-[8px] mt-[14px] nl-mono text-[10px] leading-[14px] font-medium text-[#9696a8]">
        <span>RANGE TOTAL · <span className="text-white">{fmtUsd(total)}</span></span>
        <span>RANGE PEAK · <span className="text-white">{fmtUsd(maxV)}</span></span>
        {range === "ALL" && <span data-testid="dex-monthly-note">MONTHLY SUMS OF REAL DAILY DATA</span>}
      </div>

      <div className="grid lg:grid-cols-2 gap-x-[36px] gap-y-[26px] mt-[28px] pt-[22px] border-t border-[#222234]">
        <div data-testid="dex-chain-breakdown">
          <p className="nl-geist text-[9px] md:text-[10px] leading-[14px] font-semibold tracking-[1px] text-[#22d3ee] uppercase">
            Where activity happens
          </p>
          <p className="nl-geist text-[12px] leading-[17px] text-[#9696a8] mt-[4px]">24H volume by chain</p>
          <div className="mt-[10px]">
            {(da.chain_breakdown_24h || []).map((r: any, i: number) => (
              <ShareRow key={r.name} r={r} testid={`dex-chain-row-${i}`}
                color="linear-gradient(90deg,#22d3ee,#1fe09c)" />
            ))}
          </div>
        </div>
        <div data-testid="dex-version-breakdown">
          <p className="nl-geist text-[9px] md:text-[10px] leading-[14px] font-semibold tracking-[1px] text-[#8b5cff] uppercase">
            How activity happens
          </p>
          <p className="nl-geist text-[12px] leading-[17px] text-[#9696a8] mt-[4px]">24H volume by protocol version</p>
          <div className="mt-[10px]">
            {(da.version_breakdown_24h || []).map((r: any, i: number) => (
              <ShareRow key={r.name} r={r} testid={`dex-version-row-${i}`}
                color="linear-gradient(90deg,#8b5cff,#22d3ee)" />
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-[20px] gap-y-[8px] border-t border-[#222234] mt-[24px] pt-[14px]">
        <p className="nl-geist text-[10px] md:text-[11px] leading-[15px] text-[#9499ad] max-w-[640px]" data-testid="dex-definition">
          {da.definition}
        </p>
        {da.methodology_url && (
          <a href={da.methodology_url} target="_blank" rel="noopener noreferrer"
            className="nl-mono inline-flex items-center gap-[5px] text-[8px] md:text-[9px] font-semibold uppercase text-[#9696a8] hover:text-[#22d3ee] transition-colors"
            data-testid="dex-methodology-link">
            Methodology <ExternalLink className="h-[10px] w-[10px]" />
          </a>
        )}
      </div>
    </div>
  );
};
